import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  use,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormatterType } from '@fabric-ui/components';
import { PaginationProps } from '@fabric-ui/components/Table';
import { isDeepEqual } from '@fabric-ui/utils';
import { useStateWithHistory } from '@hooks/useStateWithHistory';
import { ColumnFiltersState } from '@tanstack/react-table';
import { useLocation, useNavigate, useParams } from '@utils/AvRouter';
import { generateID } from '@utils/utils';
import { FileAnalyticsContext } from '@views/Sources/FileAnalyticsContext';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../../context/AvContextProvider';
import { PAGE_PATHS, Permission } from '../../../types';
import { useDefaultMappings, useDSIData, useMapping } from '../hooks';
import { FileParser } from '../types';
import { INSTANCE_ELIGIBLE_ENTITIES } from './constants';
import { useMappingFileFields } from './hooks/useMappingFileFields';
import { useMappingPreviewData } from './hooks/useMappingPreviewData';
import { useMappingValidation } from './MappingValidationContext';
import {
  DragItem,
  DragItemType,
  FieldMapping,
  FieldValueType,
  MapFilterType,
  MappingFieldType,
  MappingTreeNode,
  MappingType,
} from './types';
import {
  createField,
  getEntitiesShouldBeOpen,
  getFieldTypePath,
  isMappingExploded,
  isMappingItemPathMissing,
  parseDataFields,
} from './utils';

export const filterFieldsBySearch = (
  fields: MappingTreeNode[],
  searchTerm: string,
  isMappedFilterResult: (s: string) => boolean
): MappingTreeNode[] =>
  fields
    .map(field => {
      const filteredChildren = filterFieldsBySearch(field.children || [], searchTerm, isMappedFilterResult);
      return field.label.toLowerCase().includes(searchTerm) && isMappedFilterResult(field.label)
        ? field
        : filteredChildren.length
          ? { ...field, children: filteredChildren }
          : null;
    })
    .filter(Boolean) as MappingTreeNode[];

export interface MappingContextProps {
  dsiName: string;
  mappings: FieldMapping[];
  mappingsHistory: FieldMapping[][];
  mappingsCurrentStep: number;
  setMappings: Dispatch<SetStateAction<FieldMapping[]>>;
  isDirty: boolean;
  isReadOnly: boolean;
  fileParser: { fileParser: FileParser };
  setFileParser: Dispatch<SetStateAction<{ fileParser: FileParser }>>;
  mappingSearch: string;
  setMappingSearch: Dispatch<SetStateAction<string>>;
  parsedFieldsSearch: string;
  setParsedFieldsSearch: Dispatch<SetStateAction<string>>;
  parsedFieldsMappedFilter: MapFilterType | undefined;
  setParsedFieldsMappedFilter: Dispatch<SetStateAction<MapFilterType | undefined>>;
  showEditor: boolean;
  setShowEditor: Dispatch<SetStateAction<boolean>>;
  showPreview: boolean;
  setShowPreview: Dispatch<SetStateAction<boolean>>;
  previewPopperCol: string;
  setPreviewPopperCol: Dispatch<SetStateAction<string>>;
  editorValue: string | string[];
  setEditorValue: Dispatch<SetStateAction<string | string[]>>;
  editorType: MappingType;
  setEditorType: Dispatch<SetStateAction<MappingType>>;
  entityName: string | undefined;
  setEntityName: Dispatch<SetStateAction<string | undefined>>;
  editEntityField: string | undefined;
  setEditEntityField: Dispatch<SetStateAction<string | undefined>>;
  editDefaultEntityField: string | undefined;
  setDefaultEntityField: Dispatch<SetStateAction<string | undefined>>;
  editEntityFieldType: MappingFieldType | undefined;
  setEditEntityFieldType: Dispatch<SetStateAction<MappingFieldType | undefined>>;
  editorIsExploded: boolean | undefined;
  setEditorIsExploded: Dispatch<SetStateAction<boolean | undefined>>;
  fieldsList: MappingTreeNode[];
  explodedField: string | undefined;
  setExplodedField: Dispatch<SetStateAction<string | undefined>>;
  expandedMappedEntities: Record<string, boolean>;
  setExpandedMappedEntities: Dispatch<SetStateAction<Record<string, boolean>>>;
  expandedEntities: Record<string, boolean>;
  setExpandedEntities: Dispatch<SetStateAction<Record<string, boolean>>>;
  originalObject: any;
  entitiesMappedFilter: MapFilterType | undefined;
  setEntitiesMappedFilter: Dispatch<SetStateAction<MapFilterType | undefined>>;
  entitiesSearch: string;
  setEntitiesSearch: Dispatch<SetStateAction<string>>;
  dsiData: any;
  isDsiLoading: boolean;
  mappedEntityFields: MutableRefObject<Record<string, HTMLElement | null>>;
  mappedParsedFields: MutableRefObject<Record<string, HTMLElement | null>>;
  entitiesRefsMap: MutableRefObject<Record<string, HTMLElement | null>>;
  parsedFieldsRefsMap: MutableRefObject<Record<string, HTMLElement | null>>;
  originalMappings: FieldMapping[];
  getFieldType: (field: string | undefined, entityName: string | undefined) => any;
  isInSearch: (row: FieldMapping) => boolean;
  visibleMappings: FieldMapping[];
  previewData: any;
  dataFields: any;
  isLoadingFieldsPreview: boolean;
  hasFieldsDataLoaded: boolean;
  isLoadingFieldTypes: boolean;
  forceRender: () => void;
  visibleParsedFields: MappingTreeNode[];
  parsedFields: any;
  reset: () => void;
  dragItem: DragItemType | null;
  setDragItem: Dispatch<SetStateAction<DragItemType | null>>;
  handleDrop: (type: DragItem, dragItem: DragItemType) => void;
  entityDropRef: MutableRefObject<HTMLElement | undefined>;
  parsedFieldDropRef: MutableRefObject<HTMLElement | undefined>;
  undo: () => void;
  canUndo: boolean;
  redo: () => void;
  canRedo: boolean;
  clearMappings: (mappingState?: SetStateAction<FieldMapping[]>) => void;
  syncInstanceNamesBaseline: () => void;
  previewPaginationProps?: PaginationProps;
  revertToDefaultMappings: () => void;
  workflowId: string | undefined;
  isLoadingWorkflowId: boolean;
  isDefaultMappings: boolean;
  cleanState: () => void;
  isLoadingPreviewMappingQuery: boolean;
  isRefetchingPreviewMappingQuery: boolean;
  isPresentationMode: boolean;
  isLoadingMappings: boolean;
  previewRows: any[] | undefined;
  hasExplodedField: boolean;
  isStaleWorkflow: boolean;
  previewColumnFilters: ColumnFiltersState;
  setPreviewColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
  fieldTypesMap: Record<string, FormatterType>;
  instanceModeEnabled: boolean;
  setInstanceModeEnabled: Dispatch<SetStateAction<boolean>>;
  instanceEntityType: string | null;
  setInstanceEntityType: Dispatch<SetStateAction<string | null>>;
  instanceNames: Record<string, Record<number, string>>;
  setInstanceNames: Dispatch<SetStateAction<Record<string, Record<number, string>>>>;
  getInstanceName: (entityName: string, replicaNumber: number) => string;
  getInstanceCount: (entityName: string) => number;
}

export const MappingContext = React.createContext<MappingContextProps>({} as MappingContextProps);

export const MappingContextProvider = ({ children, dataSourceName }: { children: React.ReactNode; dataSourceName?: string }) => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id: instanceId, type, previewId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading: isDsiLoading, data: dsiData } = useDSIData({ instanceId });
  const dsiName = dsiData?.name ?? dataSourceName;
  const { clearValidationMessages } = useMappingValidation();

  const onHistoryChange = (newMappings: FieldMapping[] | undefined) => {
    const newExplodedField = newMappings?.find(isMappingExploded);
    const explodedMappingValue = (
      newExplodedField?.fieldValueType === FieldValueType.MappingValue
        ? newExplodedField
        : newExplodedField?.compositeValue?.fields?.find(f => f.exploded)
    )?.mappingValue;

    if (explodedMappingValue && explodedField !== explodedMappingValue) {
      setExplodedField(explodedMappingValue);
    }

    clearValidationMessages();
    reset();
  };

  const {
    currentState: mappings,
    setState: setMappingsDirect,
    clearHistory: clearMappings,
    undo,
    canUndo,
    redo,
    canRedo,
    isInitialState,
    history: mappingsHistory,
    currentStep: mappingsCurrentStep,
  } = useStateWithHistory<FieldMapping[]>(state?.mappingsHistoryState || state?.mapping || [], {
    allowKeyboardShortcuts: true,
    onUndo: onHistoryChange,
    onRedo: onHistoryChange,
  });

  const hasInitializedMappings = useRef(false);
  const hasHydratedInstanceState = useRef(false);
  const initialRouterStep = useRef<number | null>(null);
  const originalInstanceNames = useRef<Record<string, Record<number, string>>>({});
  const mappedEntityFields = useRef<Record<string, HTMLElement | null>>({});
  const mappedParsedFields = useRef<Record<string, HTMLElement | null>>({});
  const entitiesRefsMap = useRef<Record<string, HTMLElement | null>>({});
  const parsedFieldsRefsMap = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    hasHydratedInstanceState.current = false;
    hasInitializedMappings.current = false;
    initialRouterStep.current = null;
    originalInstanceNames.current = {};
  }, [instanceId]);

  const setMappings = (arg: React.SetStateAction<FieldMapping[]>) => {
    hasInitializedMappings.current = true;
    cleanState();
    setMappingsDirect(arg);
  };

  const isDirty = useMemo(() => {
    if (initialRouterStep.current !== null) {
      return mappingsCurrentStep !== initialRouterStep.current;
    }
    return !isInitialState;
  }, [isInitialState, mappingsCurrentStep]);

  const entitiesShouldBeOpen = state?.mapping || state?.mappingsHistoryState ? getEntitiesShouldBeOpen(mappings) : { General: true };
  const [fileParser, setFileParser] = useState<{ fileParser: FileParser }>(state?.fileParser);
  const [mappingSearch, setMappingSearch] = useState('');
  const [parsedFieldsSearch, setParsedFieldsSearch] = useState('');
  const [parsedFieldsMappedFilter, setParsedFieldsMappedFilter] = useState<MapFilterType>();
  const [editorValue, setEditorValue] = useState<string | string[]>('');
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editorType, setEditorType] = useState<MappingType>(MappingType.Script);
  const [entityName, setEntityName] = useState<string>();
  const [editEntityField, setEditEntityField] = useState<string>();
  const [editDefaultEntityField, setDefaultEntityField] = useState<string>();
  const [editEntityFieldType, setEditEntityFieldType] = useState<MappingFieldType>();
  const [previewPopperCol, setPreviewPopperCol] = useState<string>('');
  const [expandedEntities, setExpandedEntities] = useState(entitiesShouldBeOpen);
  const [originalObject, setOriginalObject] = useState<any>({});
  const [entitiesMappedFilter, setEntitiesMappedFilter] = useState<MapFilterType>();
  const [entitiesSearch, setEntitiesSearch] = useState('');

  const [editorIsExploded, setEditorIsExploded] = useState<boolean>();
  const [expandedMappedEntities, setExpandedMappedEntities] = useState<Record<string, boolean>>(entitiesShouldBeOpen);
  const [explodedField, setExplodedField] = useState<string | undefined>(undefined);

  const [instanceModeEnabled, setInstanceModeEnabled] = useState(false);
  const [instanceEntityType, setInstanceEntityType] = useState<string | null>(null);
  const [instanceNames, setInstanceNames] = useState<Record<string, Record<number, string>>>({});
  const isInstanceNamesDirty = !isDeepEqual(instanceNames, originalInstanceNames.current);
  const syncInstanceNamesBaseline = useCallback(() => {
    originalInstanceNames.current = instanceNames;
  }, [instanceNames]);

  const {
    userPermissions: { hasOnlyPermission },
    accountEntities: { oldIngressProjs: entityFields, fieldTypeMap },
  } = useAvContext();

  const entityDropRef = useRef<HTMLElement>(undefined);
  const parsedFieldDropRef = useRef<HTMLElement>(undefined);
  const [dragItem, setDragItem] = useState<DragItemType | null>(null);
  const isReadOnly = hasOnlyPermission({ path: PAGE_PATHS.SOURCES_MAPPING, permission: Permission.READ });

  const cleanState = useCallback(() => {
    const { mapping, fileParser, ...newState } = state || {};
    navigate({ state: newState, ignoreBlocker: true });
  }, [state, navigate]);

  const { isLoadingWorkflowId, workflowId, isStaleWorkflow } = use(FileAnalyticsContext);

  const onSuccess = data => {
    setOriginalObject(data);
    if (!hasHydratedInstanceState.current) {
      hydrateInstanceState(data.replicaMetadata ?? []);
      hasHydratedInstanceState.current = true;
    }
    if (!hasInitializedMappings.current) {
      hasInitializedMappings.current = true;
      if (state?.mappingsHistoryState) {
        initialRouterStep.current = state.mappingsHistoryState.currentStep;
        const entitiesShouldBeOpen = getEntitiesShouldBeOpen(mappings);
        setExpandedMappedEntities(entitiesShouldBeOpen);
        setExpandedEntities(entitiesShouldBeOpen);
        updateExplodedField(mappings);
      } else {
        clearMappings(data.originalMappings);
        const entitiesShouldBeOpen = getEntitiesShouldBeOpen(data.originalMappings);
        setExpandedMappedEntities(entitiesShouldBeOpen);
        setExpandedEntities(entitiesShouldBeOpen);
        updateExplodedField(data.originalMappings);
      }
    }
  };

  const hydrateInstanceState = (replicaMetadata: { entityName: string; replicaNumber: number | null; instanceName: string }[]) => {
    const names: Record<string, Record<number, string>> = {};
    replicaMetadata.forEach(({ entityName, replicaNumber, instanceName }) => {
      if (!names[entityName]) {
        names[entityName] = {};
      }
      // Backend may send replicaNumber: null for the base replica — normalise to 0
      names[entityName][replicaNumber ?? 0] = instanceName;
    });
    const entityKeys = Object.keys(names);
    if (entityKeys.length > 0) {
      setInstanceModeEnabled(true);
      setInstanceEntityType(entityKeys[0]);
      setInstanceNames(names);
      originalInstanceNames.current = names;
    }
  };

  const updateExplodedField = (newMappings: FieldMapping[]) => {
    const newExplodedFieldPath = newMappings.reduce(
      (field, mapping) =>
        field ??
        (mapping.mappingType === MappingType.Path && mapping.exploded
          ? mapping.mappingValue
          : mapping.mappingType === MappingType.Composite
            ? mapping.compositeValue?.fields.find(field => field.exploded)?.mappingValue
            : undefined),
      undefined as string | undefined
    );

    if (!newExplodedFieldPath) {
      setExplodedField(undefined);
      return;
    }

    const fieldTypesMap = getFieldTypePath(fieldsList, newExplodedFieldPath);
    const pathSegments = newExplodedFieldPath.split('.');

    const firstArrayInPath = pathSegments.reduce(
      (foundPath, _, index, arr) => {
        if (foundPath) {
          return foundPath;
        }

        const currentSubPath = arr.slice(0, index + 1).join('.');
        if (fieldTypesMap[currentSubPath] === FormatterType.array) {
          return currentSubPath;
        }
        return null;
      },
      null as string | null
    );

    setExplodedField(firstArrayInPath ?? newExplodedFieldPath);
  };

  const isInSearch = useCallback(
    (fieldMapping: FieldMapping) => {
      const { entityName, modelFieldName, fieldValueType, mappingValue, compositeValue } = fieldMapping;
      if (!mappingSearch) {
        return true;
      }

      return [
        entityName,
        modelFieldName,
        fieldValueType === FieldValueType.MappingValue
          ? mappingValue
          : compositeValue.fields?.map(({ mappingValue }) => mappingValue).join(' '),
        entityFields[entityName].nameMap[modelFieldName],
      ]
        .map(v => v?.toLowerCase().trim())
        .some(v => v?.includes(mappingSearch.toLowerCase().trim()));
    },
    [mappingSearch]
  );

  const visibleMappings = useMemo(
    () =>
      mappings.filter(
        mapRow =>
          // filter those who are collapsed / in the scroll area or not in search.
          isInSearch(mapRow) && expandedEntities[mapRow.entityName] && expandedMappedEntities[mapRow.entityName]
      ),
    [mappingSearch, mappings, expandedEntities, expandedMappedEntities]
  );

  const { data: { originalMappings } = {}, isLoading: isLoadingDsiMappings } = useMapping(type, previewId, instanceId, onSuccess);

  const { data: defaultMappings, isLoading: isLoadingDefaultMappings } = useDefaultMappings(type);

  const getFieldType = useCallback(
    (field: string | undefined, entityName: string | undefined) =>
      fieldTypeMap[`${(entityName && entityFields[entityName]?.projId?.name) ?? entityName?.toLowerCase()}.${field ?? ''}`],
    [fieldTypeMap, entityFields]
  );

  useEffect(() => {
    if (state?.fileParser || dsiData) {
      setFileParser(state?.fileParser || dsiData);
    }
  }, [state?.fileParser, dsiData]);

  const [page, setPage] = useState(0);
  const [previewColumnFilters, setPreviewColumnFilters] = useState<ColumnFiltersState>([]);

  const {
    fields,
    fieldTypesMap,
    keys,
    hasLoaded: hasFieldsDataLoaded,
    isLoading: isLoadingFieldsData,
    isLoadingFieldTypes,
  } = useMappingFileFields({
    instanceId,
    workflowId,
    enabled: !isLoadingWorkflowId && !isStaleWorkflow,
  });

  const {
    data: queryMappingPreviewData,
    totalRowCount: totalRowsCount,
    isLoading: isLoadingPreviewMappingQuery,
    isRefetching: isRefetchingPreviewMappingQuery,
  } = useMappingPreviewData({
    page,
    workflowId,
    enabled: !isLoadingWorkflowId,
    instanceId,
    columnFilters: previewColumnFilters,
    fieldTypesMap,
  });

  const dataFields = useMemo<MappingTreeNode[]>(() => (isStaleWorkflow ? [] : fields), [isStaleWorkflow, fields]);
  const previewData = (queryMappingPreviewData?.length ?? 0) > 0 ? queryMappingPreviewData : [];

  const parsedFields = useMemo<string[]>(() => keys, [keys]);

  const previewRows = useMemo(() => {
    if (!previewData) return undefined;
    try {
      return previewData.map((row, index) => ({ ...(typeof row === 'string' ? JSON.parse(row) : row), avalor_unique_key: index }));
    } catch (e) {
      console.error({ e, previewData });
      enqueueSnackbar('Unable to parse fields, try to change parsing', { variant: 'error' });
      return [];
    }
  }, [previewData, enqueueSnackbar]);

  const isLoadingFieldsPreview = isLoadingFieldsData;

  const [, updateState] = useState<number>();
  const forceRender = () => {
    requestAnimationFrame(() => updateState(Date.now()));
  };
  const reset = useCallback(() => {
    setDragItem(null);
    setEditEntityField(undefined);
    setDefaultEntityField(undefined);
    setEditorValue('');
    setEditorIsExploded(false);
    setEntityName(undefined);
    setShowEditor(false);
    setEditorType(MappingType.Script);
  }, []);

  const isDefaultMappings = useMemo(() => isDeepEqual(defaultMappings, mappings), [defaultMappings, mappings]);

  const revertToDefaultMappings = useCallback(() => {
    if (!isDefaultMappings && defaultMappings) {
      setMappings(defaultMappings);
      setExpandedMappedEntities(getEntitiesShouldBeOpen(defaultMappings));
      reset();
    }
  }, [setMappings, isDefaultMappings, defaultMappings, reset]);

  const parsedPreviewData = useMemo(() => previewData?.[0] ?? [], [previewData]);

  useEffect(() => {
    updateExplodedField(mappings);
  }, [dataFields]);

  const fieldsList = useMemo(() => {
    // Map of missing path -> whether any mapping referencing it has exploded:true.
    // De-duplicates paths (Map keys) and "ORs" the exploded flag when the same path
    // appears in multiple mappings.
    const missingMappedMap = mappings.reduce((map, { fieldValueType, mappingType, mappingValue, exploded, compositeValue }) => {
      if (
        mappingType === MappingType.Path &&
        fieldValueType === FieldValueType.MappingValue &&
        typeof mappingValue === 'string' &&
        isMappingItemPathMissing(mappingValue, parsedFields, parsedPreviewData)
      ) {
        map.set(mappingValue, map.get(mappingValue) || !!exploded);
      }
      if (mappingType === MappingType.Composite) {
        compositeValue?.fields?.forEach(f => {
          if (typeof f.mappingValue === 'string' && isMappingItemPathMissing(f.mappingValue, parsedFields, parsedPreviewData)) {
            map.set(f.mappingValue, map.get(f.mappingValue) || !!f.exploded);
          }
        });
      }
      return map;
    }, new Map<string, boolean>());

    return [
      ...parseDataFields(dataFields, explodedField),
      ...[...missingMappedMap].map(([path, isExploded]) => createField(path, true, isExploded)),
    ];
  }, [dataFields, explodedField, mappings, parsedFields, parsedPreviewData]);

  const visibleParsedFields = useMemo(() => {
    const isMappedFilterResult = v => {
      const mappedParsedFields = mappings.map(mapRow => mapRow.mappingValue);
      return (
        !parsedFieldsMappedFilter ||
        (parsedFieldsMappedFilter === MapFilterType.Mapped ? mappedParsedFields.includes(v) : !mappedParsedFields.includes(v))
      );
    };

    return filterFieldsBySearch(fieldsList, parsedFieldsSearch.toLowerCase(), isMappedFilterResult);
  }, [fieldsList, mappings, parsedFieldsMappedFilter, parsedFieldsSearch]);

  const handleDrop = useCallback(
    (type: DragItem, dragItem) => {
      if (type === dragItem.type) {
        const label = dragItem.field.path || dragItem.field.label || dragItem.field;
        if (type === DragItem.Parsed) {
          setEditorValue(label);
          setEditorType(MappingType.Path);
          setShowEditor(false);
          setEditorIsExploded(dragItem.field.isExploded);
        }
        if (type === DragItem.Entity) {
          const isInstanceModeRemap = instanceModeEnabled && INSTANCE_ELIGIBLE_ENTITIES.has(dragItem.entityName);
          if (!isInstanceModeRemap && mappings.some(row => row.modelFieldName === label && row.entityName === dragItem.entityName)) {
            enqueueSnackbar('This field is already mapped', { variant: 'warning' });
            return;
          }
          setEditEntityField(label);
          setDefaultEntityField(generateID());
          setEntityName(dragItem.entityName);
        }
      }
      setDragItem(null);
    },
    [mappings, instanceModeEnabled]
  );

  const hasExplodedField = useMemo(
    () => mappings.some(({ exploded, compositeValue }) => exploded || compositeValue?.fields?.some(field => field.exploded)),
    [mappings]
  );

  const getInstanceName = useCallback(
    (entityName: string, replicaNumber: number) =>
      instanceNames[entityName]?.[replicaNumber] ?? `${entityName} - Instance ${replicaNumber + 1}`,
    [instanceNames]
  );

  const getInstanceCount = useCallback(
    (entityName: string) => {
      const maxReplica = mappings
        .filter(m => m.entityName === entityName)
        .reduce((max, { replicaNumber }) => Math.max(replicaNumber ?? 0, max), 0);
      return maxReplica + 1;
    },
    [mappings]
  );

  const value = useMemo(
    () => ({
      dsiName,
      mappings,
      mappingsHistory,
      mappingsCurrentStep,
      setMappings,
      isDirty: isDirty || isInstanceNamesDirty,
      isReadOnly,
      fileParser,
      setFileParser,
      mappingSearch,
      setMappingSearch,
      dsiData,
      isDsiLoading,
      parsedFieldsSearch,
      setParsedFieldsSearch,
      parsedFieldsMappedFilter,
      setParsedFieldsMappedFilter,
      showEditor,
      setShowEditor,
      showPreview,
      setShowPreview,
      previewPopperCol,
      setPreviewPopperCol,
      mappedEntityFields,
      mappedParsedFields,
      editorValue,
      setEditorValue,
      editorType,
      setEditorType,
      entityName,
      setEntityName,
      editEntityField,
      setEditEntityField,
      editDefaultEntityField,
      setDefaultEntityField,
      editEntityFieldType,
      setEditEntityFieldType,
      editorIsExploded,
      setEditorIsExploded,
      expandedMappedEntities,
      setExpandedMappedEntities,
      expandedEntities,
      setExpandedEntities,
      originalObject,
      setOriginalObject,
      originalMappings,
      getFieldType,
      isInSearch,
      visibleMappings,
      entitiesMappedFilter,
      setEntitiesMappedFilter,
      entitiesSearch,
      setEntitiesSearch,
      entitiesRefsMap,
      parsedFieldsRefsMap,
      previewData,
      dataFields,
      isLoadingFieldsPreview,
      hasFieldsDataLoaded,
      isLoadingFieldTypes,
      forceRender,
      visibleParsedFields,
      parsedFields,
      fieldsList,
      explodedField,
      setExplodedField,
      reset,
      dragItem,
      setDragItem,
      handleDrop,
      entityDropRef,
      parsedFieldDropRef,
      undo,
      canUndo,
      redo,
      canRedo,
      clearMappings,
      syncInstanceNamesBaseline,
      revertToDefaultMappings,
      isDefaultMappings,
      isLoadingPreviewMappingQuery,
      isRefetchingPreviewMappingQuery,
      cleanState,
      previewPaginationProps: { totalRows: totalRowsCount, pageNum: page, onPageChange: setPage },
      workflowId,
      isLoadingWorkflowId,
      isPresentationMode: false,
      isLoadingMappings: isLoadingDsiMappings || isLoadingDefaultMappings,
      previewRows,
      hasExplodedField,
      isStaleWorkflow,
      fieldTypesMap,
      previewColumnFilters,
      setPreviewColumnFilters,
      instanceModeEnabled,
      setInstanceModeEnabled,
      instanceEntityType,
      setInstanceEntityType,
      instanceNames,
      setInstanceNames,
      getInstanceName,
      getInstanceCount,
    }),
    [
      dsiName,
      mappings,
      mappingsHistory,
      mappingsCurrentStep,
      setMappings,
      isDirty,
      isInstanceNamesDirty,
      isReadOnly,
      fileParser,
      mappingSearch,
      dsiData,
      isDsiLoading,
      parsedFieldsSearch,
      parsedFieldsMappedFilter,
      editorValue,
      showEditor,
      showPreview,
      editorType,
      previewPopperCol,
      entityName,
      editEntityField,
      editDefaultEntityField,
      editEntityFieldType,
      editorIsExploded,
      expandedMappedEntities,
      expandedEntities,
      originalObject,
      originalMappings,
      isInSearch,
      visibleMappings,
      entitiesMappedFilter,
      entitiesSearch,
      previewData,
      dataFields,
      isLoadingFieldsPreview,
      hasFieldsDataLoaded,
      isLoadingFieldTypes,
      getFieldType,
      visibleParsedFields,
      parsedFields,
      fieldsList,
      explodedField,
      reset,
      dragItem,
      handleDrop,
      undo,
      canUndo,
      redo,
      canRedo,
      clearMappings,
      syncInstanceNamesBaseline,
      revertToDefaultMappings,
      isDefaultMappings,
      isLoadingPreviewMappingQuery,
      isRefetchingPreviewMappingQuery,
      cleanState,
      totalRowsCount,
      page,
      workflowId,
      isLoadingWorkflowId,
      isLoadingDsiMappings,
      isLoadingDefaultMappings,
      previewRows,
      hasExplodedField,
      isStaleWorkflow,
      fieldTypesMap,
      previewColumnFilters,
      setPreviewColumnFilters,
      instanceModeEnabled,
      instanceEntityType,
      instanceNames,
      getInstanceName,
      getInstanceCount,
    ]
  );

  return <MappingContext value={value}>{children}</MappingContext>;
};

export const useMappingContext = () => useContext(MappingContext);
